<template>
  <v-app>
    <v-container>
      <v-row class="mb-3">
        <v-col cols="12">
          <h2>All-day 事件折叠测试</h2>
          <p class="caption grey--text">每日最多显示 {{ maxVisible }} 个 all-day 事件，超过的可展开/收合</p>
        </v-col>
      </v-row>

      <v-row class="mb-3">
        <v-col>
          <v-btn small outlined class="mr-2" @click="today">今天</v-btn>
          <v-btn icon @click="prev"><v-icon>mdi-chevron-left</v-icon></v-btn>
          <v-btn icon @click="next"><v-icon>mdi-chevron-right</v-icon></v-btn>
          <span class="ml-3">{{ calendarTitle }}</span>
        </v-col>
      </v-row>

      <v-sheet height="700">
        <v-calendar
          ref="calendar"
          v-model="focus"
          type="week"
          :weekdays="[1,2,3,4,5,6,0]"
          first-interval="8"
          interval-count="12"
          interval-minutes="60"
          :events="displayEvents"
          :event-color="e => e.color || 'primary'"
          @click:event="handleEventClick"
        >
          <!-- 自定义事件渲染 -->
          <template #event="{ event }">
            <!-- 展开按钮 -->
            <div v-if="event._isExpandBtn" class="pa-1">
              <v-btn x-small text color="primary" @click.stop="expandDay(event._date)">
                {{ event.name }}
              </v-btn>
            </div>
            <!-- 收合按钮 -->
            <div v-else-if="event._isCollapseBtn" class="pa-1">
              <v-btn x-small text color="grey" @click.stop="collapseDay(event._date)">
                {{ event.name }}
              </v-btn>
            </div>
            <!-- 一般事件 -->
            <div v-else class="px-2 py-1">
              <strong class="d-block text-truncate">{{ event.name }}</strong>
              <span v-if="event.timed" class="caption">
                {{ formatTime(event.start) }} - {{ formatTime(event.end) }}
              </span>
            </div>
          </template>
        </v-calendar>
      </v-sheet>

      <!-- Debug 信息 -->
      <v-row class="mt-3">
        <v-col>
          <v-card outlined>
            <v-card-title>Debug 信息</v-card-title>
            <v-card-text>
              <div><strong>展开的日期：</strong>{{ Object.keys(expandedDates).join(', ') || '无' }}</div>
              <div><strong>总事件数：</strong>{{ displayEvents.length }}</div>
              <div><strong>All-day 事件数：</strong>{{ allDayEvents.length }}</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
export default {
  name: 'CalendarTest',
  data() {
    return {
      focus: '2025-11-05',
      maxVisible: 2, // 每日最多显示的 all-day 事件数
      expandedDates: {}, // { '2025-11-05': true }

      // 测试数据
      rawEvents: [
        // 2025-11-05 的 all-day 事件（5个）
        { name: '张三 - 3M提醒', start: '2025-11-05', timed: false, color: '#FF8C42' },
        { name: '李四 - 6M提醒', start: '2025-11-05', timed: false, color: '#FF8C42' },
        { name: '王五 - 额外提醒', start: '2025-11-05', timed: false, color: '#2196F3' },
        { name: '赵六 - 3M提醒', start: '2025-11-05', timed: false, color: '#FF8C42' },
        { name: '钱七 - 备注', start: '2025-11-05', timed: false, color: '#9E9E9E' },

        // 2025-11-06 的 all-day 事件（3个）
        { name: '孙八 - 6M提醒', start: '2025-11-06', timed: false, color: '#FF8C42' },
        { name: '周九 - 额外提醒', start: '2025-11-06', timed: false, color: '#2196F3' },
        { name: '吴十 - 3M提醒', start: '2025-11-06', timed: false, color: '#FF8C42' },

        // 普通时间事件（不受折叠影响）
        { name: '会议', start: '2025-11-05 09:00', end: '2025-11-05 10:00', timed: true, color: 'green' },
        { name: '诊疗', start: '2025-11-05 14:00', end: '2025-11-05 15:30', timed: true, color: 'indigo' },
      ],
    };
  },
  computed: {
    calendarTitle() {
      return this.$refs.calendar ? this.$refs.calendar.title : '';
    },

    // 所有 all-day 事件
    allDayEvents() {
      return this.rawEvents.filter(e => !e.timed);
    },

    // 按日期分组的 all-day 事件
    allDayByDate() {
      const grouped = {};
      this.allDayEvents.forEach(event => {
        const date = this.toDateStr(event.start);
        if (!grouped[date]) grouped[date] = [];
        grouped[date].push(event);
      });
      return grouped;
    },

    // 最终显示的事件列表
    displayEvents() {
      console.log('[displayEvents] 重新计算');
      console.log('[displayEvents] expandedDates:', JSON.stringify(this.expandedDates));

      const result = [];

      // 1. 加入所有时间事件（不受折叠影响）
      const timedEvents = this.rawEvents.filter(e => e.timed);
      result.push(...timedEvents);

      // 2. 处理每一天的 all-day 事件
      Object.keys(this.allDayByDate).forEach(date => {
        const events = this.allDayByDate[date];
        const isExpanded = !!this.expandedDates[date];

        console.log(`[displayEvents] ${date}: ${events.length} 个事件, 展开=${isExpanded}`);

        if (events.length <= this.maxVisible) {
          // 事件数量不多，全部显示
          result.push(...events);
        } else if (isExpanded) {
          // 展开状态：显示所有事件 + 收合按钮
          result.push(...events);
          result.push({
            name: '▲ 收合',
            start: date,
            end: date,
            timed: false,
            color: 'grey lighten-2',
            _isCollapseBtn: true,
            _date: date,
          });
        } else {
          // 折叠状态：显示前 N 个 + 展开按钮
          const visible = events.slice(0, this.maxVisible);
          const hiddenCount = events.length - visible.length;

          result.push(...visible);
          result.push({
            name: `▼ +${hiddenCount} 更多`,
            start: date,
            end: date,
            timed: false,
            color: 'blue lighten-4',
            _isExpandBtn: true,
            _date: date,
          });
        }
      });

      console.log('[displayEvents] 结果数量:', result.length);
      return result;
    },
  },
  methods: {
    // 日期字符串转换
    toDateStr(dateStr) {
      if (typeof dateStr === 'string' && dateStr.length === 10) {
        return dateStr; // 已经是 yyyy-mm-dd 格式
      }
      const d = new Date(dateStr);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },

    // 时间格式化
    formatTime(timeStr) {
      const d = new Date(timeStr);
      const h = String(d.getHours()).padStart(2, '0');
      const m = String(d.getMinutes()).padStart(2, '0');
      return `${h}:${m}`;
    },

    // 展开某一天
    expandDay(date) {
      console.log('[expandDay]', date);
      this.$set(this.expandedDates, date, true);
    },

    // 收合某一天
    collapseDay(date) {
      console.log('[collapseDay]', date);
      this.$delete(this.expandedDates, date);
    },

    // 事件点击（用于调试）
    handleEventClick({ event }) {
      if (event._isExpandBtn || event._isCollapseBtn) return;
      console.log('[handleEventClick]', event.name);
    },

    // 日历导航
    today() {
      this.focus = new Date().toISOString().substr(0, 10);
    },
    prev() {
      this.$refs.calendar.prev();
    },
    next() {
      this.$refs.calendar.next();
    },
  },
};
</script>

<style scoped>
/* 让按钮事件看起来不同 */
.v-event .v-btn {
  min-width: 0;
  text-transform: none;
}
</style>
